import * as React from "react";
import {
    Box,
    Button,
    Grid,
    Header,
    Select,
    SpaceBetween,
    Toggle
} from "@amzn/awsui-components-react";
import { UIModel } from "../../../../../model/ui-model";
import {
    AdvancedListContent,
    Identity,
    ItemAccessControl,
    Team
} from "@amzn/ask-legal-domain";
import {
    AdvancedList,
    IdentityRef,
    IdentityType,
    IdentityRealm,
    FieldDefinitionDataType
} from "@amzn/altar-sds-client";
import { Builder } from "builder-pattern";
import { UserSearch } from "../../../../common/UserSearch";
import { TeamSearch } from "../../../../common/TeamSearch";
import { UIField } from "../../../../common/UIField";
import { IdentityToken } from "../../../../common/IdentityToken";

export const Permission = (props: {
    state: UIModel.State<AdvancedListContent>;
    advancedList: AdvancedList;
}) => {
    const [identityRefs, setIdentityRefs] = React.useState<IdentityRef[]>([
        ...(props.state?.value?.updateAdvancedListPayload?.addEditorsList || []),
        ...(props.advancedList?.editors?.filter(e => !props.state?.value?.updateAdvancedListPayload?.removeEditorsList?.find(r => r.id === e.id)) || [])
    ]);
    const [editorFieldKey, setEditorFieldKey] = React.useState<string>(
        (props.state?.value?.updateAdvancedListPayload && "editorFieldKey" in props.state.value.updateAdvancedListPayload) ?
            props.state.value.updateAdvancedListPayload.editorFieldKey :
            props.advancedList?.editorFieldKey
    );
    const [viewFieldKey, setViewFieldKey] = React.useState<string>(
        (props.state?.value?.updateAdvancedListPayload && "viewerFieldKey" in props.state.value.updateAdvancedListPayload) ?
            props.state.value.updateAdvancedListPayload.viewerFieldKey :
            props.advancedList?.viewerFieldKey
    );

    // Re-initialize local state when advancedList becomes available (e.g., after
    // first publish). Without this, the state variables hold stale initial values
    // (empty arrays / undefined) from when advancedList was null.
    React.useEffect(() => {
        if (!props.advancedList) return;
        setIdentityRefs([
            ...(props.state?.value?.updateAdvancedListPayload?.addEditorsList || []),
            ...(props.advancedList.editors?.filter(e =>
                !props.state?.value?.updateAdvancedListPayload?.removeEditorsList?.find(r => r.id === e.id)
            ) || [])
        ]);
        if (!(props.state?.value?.updateAdvancedListPayload && "editorFieldKey" in props.state.value.updateAdvancedListPayload)) {
            setEditorFieldKey(props.advancedList.editorFieldKey ?? "");
        }
        if (!(props.state?.value?.updateAdvancedListPayload && "viewerFieldKey" in props.state.value.updateAdvancedListPayload)) {
            setViewFieldKey(props.advancedList.viewerFieldKey ?? "");
        }
    }, [props.advancedList]);

    // Merged into a single effect to ensure atomic setValue when multiple local
    // state variables change in the same render cycle (e.g., re-init effect
    // sets identityRefs + editorFieldKey + viewFieldKey together).
    // UIModel.State.setValue doesn't support functional updaters, so a single
    // effect with one setValue call is the cleanest way to avoid races.
    React.useEffect(() => {
        if (!props.advancedList) return;
        props.state.setValue({
            ...props.state.value,
            updateAdvancedListPayload: {
                ...props.state.value.updateAdvancedListPayload,
                addEditorsList: identityRefs.filter(i => !props.advancedList.editors?.find(e => e.id === i.id)),
                removeEditorsList: props.advancedList.editors?.filter(e => !identityRefs.find(i => i.id === e.id)) || [],
                editorFieldKey: editorFieldKey,
                viewerFieldKey: viewFieldKey,
            }
        });
    }, [identityRefs, editorFieldKey, viewFieldKey]);

    // Shared toggle for item create permissions — used in both pre-publish
    // (advancedList not yet created) and post-publish views.
    const createPermissionsSection = (
        <>
            <Header variant="h2">Create Permissions</Header>
            <UIField.CustomField
                name="Item Creation Access"
                description="Editors can always create items. Enable this to also allow all page viewers to create new items."
                child={
                    <Toggle
                        checked={ItemAccessControl.isGrantedToAllPageViewers(
                            props.state.value?.itemAccessControl,
                            "createItem"
                        )}
                        onChange={({ detail }) => {
                            props.state.setValue({
                                ...props.state.value,
                                itemAccessControl: {
                                    ...props.state.value?.itemAccessControl,
                                    createItem: detail.checked ? "allPageViewers" : "editorsOnly"
                                }
                            });
                        }}
                    >
                        Allow all page viewers to create new items
                    </Toggle>
                }
            />
        </>
    );

    if (!props.advancedList) {
        return (
            <SpaceBetween direction="vertical" size="s">
                {createPermissionsSection}
                <Box color="text-status-info">
                    Editor and column permission configuration will be available after the page is published.
                </Box>
            </SpaceBetween>
        );
    }

    return (<>
        <SpaceBetween direction="vertical" size="s">
            <Header variant="h2">Configure Editors</Header>
            <Box>Editors can create, edit, and delete all List Items</Box>
            <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
                <UIField.CustomField
                    name="Editor Users"
                    child={
                        <SpaceBetween size="xs">
                            <UserSearch.Single
                                selected={null}
                                onUserSelectChange={(user) => {
                                    if (user && !identityRefs.find(i => i.id === user.id)) {
                                        setIdentityRefs(
                                            [...identityRefs, getIdentityRefFromIdentity(user)]
                                        );
                                    }
                                }}
                            />
                            <SpaceBetween direction="horizontal" size="m">
                                {identityRefs
                                    .filter(i => i.type === IdentityType.PERSON)
                                    .map(i => <IdentityToken
                                        key={i.id}
                                        identityRef={i}
                                        onRemove={(id) =>
                                            setIdentityRefs(identityRefs.filter(i => i.id !== id))
                                        }
                                    />)
                                }
                            </SpaceBetween>
                        </SpaceBetween>
                    }
                />
                <UIField.CustomField
                    name="Editor Teams"
                    child={
                        <SpaceBetween size="s">
                            <TeamSearch.Single
                                selected={null}
                                onTeamSelectChange={(team) => {
                                    if (team && !identityRefs.find(i => i.id === team.teamId)) {
                                        setIdentityRefs(
                                            [...identityRefs, getIdentityRefFromTeam(team)]
                                        );
                                    }
                                }}
                            />
                            <SpaceBetween direction="horizontal" size="xs">
                                {identityRefs
                                    .filter(i => i.type === IdentityType.TEAM)
                                    .map(i => <IdentityToken
                                        key={i.id}
                                        identityRef={i}
                                        onRemove={(id) =>
                                            setIdentityRefs(identityRefs.filter(i => i.id !== id))
                                        }
                                    />)
                                }
                            </SpaceBetween>
                        </SpaceBetween>
                    }
                />
            </Grid>

            <UIField.CustomField
                name="Editor Column"
                description="A user matching an Item's Editor Column value is able to edit and delete that specific Item.  "
                child={
                    <Grid
                        gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}
                    >
                        <Select
                            options={props.advancedList.fieldDefinitions
                                ?.filter((c) => (
                                    ([
                                        FieldDefinitionDataType.IdentityRef,
                                        FieldDefinitionDataType.multiIdentityRef
                                    ] as string[]).includes(c.dataType)
                                    && !c.deprecated
                                ))
                                .map((d) => ({
                                    value: d.fieldKey,
                                    label: d.displayName,
                                }))
                            }
                            selectedOption={editorFieldKey && {
                                value: editorFieldKey,
                                label: props.advancedList.fieldDefinitions?.find(
                                    (f) => f.fieldKey === editorFieldKey
                                )?.displayName,
                            }}
                            onChange={(e) =>
                                setEditorFieldKey(e.detail.selectedOption.value)
                            }
                            placeholder="Select a field"
                            empty="No fields available"
                        />
                        <Button
                            onClick={() => setEditorFieldKey("")}
                        >Reset</Button>
                    </Grid>
                }
            />

            <UIField.CustomField
                name="Viewer Column"
                description="A user matching an Item's Viewer Column value is able to view that specific Item."
                child={
                    <Grid
                        gridDefinition={[{ colspan: 9 }, { colspan: 3 }]}
                    >
                        <Select
                            options={props.advancedList.fieldDefinitions
                                ?.filter((c) => (
                                    ([
                                        FieldDefinitionDataType.IdentityRef,
                                        FieldDefinitionDataType.multiIdentityRef
                                    ] as string[]).includes(c.dataType)
                                    && !c.deprecated
                                ))
                                .map((d) => ({
                                    value: d.fieldKey,
                                    label: d.displayName,
                                }))
                            }
                            selectedOption={viewFieldKey && {
                                value: viewFieldKey,
                                label: props.advancedList.fieldDefinitions?.find(
                                    (f) => f.fieldKey === viewFieldKey
                                )?.displayName,
                            }}
                            onChange={(e) =>
                                setViewFieldKey(e.detail.selectedOption.value)
                            }
                            placeholder="Select a field"
                            empty="No fields available"
                        />
                        <Button
                            onClick={() => setViewFieldKey("")}
                        >Reset</Button>
                    </Grid>
                }
            />

            {createPermissionsSection}
        </SpaceBetween>
    </>);
};

function getIdentityRefFromTeam(team: Team) {
    return Builder<IdentityRef>()
        .id(team.teamId)
        .realm(IdentityRealm.AMAZON)
        .type(IdentityType.TEAM)
        .build();
}

function getIdentityRefFromIdentity(identity: Identity) {
    return Builder<IdentityRef>()
        .id(identity.id)
        .realm(IdentityRealm.AMAZON)
        .type(IdentityType.PERSON)
        .build();
}